/**
 * Home Page — GE Lines Pte Ltd
 *
 * Public landing page with hero, services, track-and-trace CTA.
 * Real info sourced from https://gelines.com
 */

import { Link } from 'react-router-dom';
import {
  Ship,
  Plane,
  Truck,
  FileCheck,
  Search,
  ArrowRight,
  MapPin,
  PackagePlus,
  SearchCheck,
  MessageSquare,
} from 'lucide-react';
import { useStorefront } from '@/context/StorefrontContext';
import { resolvePortalTheme } from '@/lib/portal-theme';

const ICON_MAP = {
  ship: Ship,
  plane: Plane,
  truck: Truck,
  'file-check': FileCheck,
} as const;

const INDUSTRY_HERO_MEDIA: Record<string, { url: string; alt: string; location: string; note: string }> = {
  haulier: {
    url: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=1800&q=85',
    alt: 'Prime mover and container trucking operation',
    location: 'Container haulage operations',
    note: 'Prime mover · container · delivery order',
  },
  transport: {
    url: 'https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?auto=format&fit=crop&w=1800&q=85',
    alt: 'Local delivery vehicles and parcel logistics',
    location: 'Local transport and delivery',
    note: 'Dispatch · route · proof of delivery',
  },
  warehouse: {
    url: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1800&q=85',
    alt: 'Warehouse storage and cargo handling',
    location: 'Warehouse operations',
    note: 'Receiving · inventory · fulfilment',
  },
  depot: {
    url: 'https://images.unsplash.com/photo-1494412651409-8963ce7935a7?auto=format&fit=crop&w=1800&q=85',
    alt: 'Container yard and depot movement',
    location: 'Depot and yard operations',
    note: 'Gate · inspection · release',
  },
  chemical_logistics: {
    url: 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=1800&q=85',
    alt: 'Industrial chemical logistics operation',
    location: 'Chemical logistics',
    note: 'ISO tank · DG · safety',
  },
  cold_chain: {
    url: 'https://images.unsplash.com/photo-1586528116493-a029325540fa?auto=format&fit=crop&w=1800&q=85',
    alt: 'Cold chain logistics handling',
    location: 'Cold chain operations',
    note: 'Reefer · temperature · traceability',
  },
  project_cargo: {
    url: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1800&q=85',
    alt: 'Heavy cargo and project site operation',
    location: 'Project cargo operations',
    note: 'Heavy lift · site · special handling',
  },
  total_logistics: {
    url: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?auto=format&fit=crop&w=1800&q=85',
    alt: 'Integrated logistics and port operation',
    location: 'Integrated logistics control tower',
    note: 'Transport · warehouse · freight · finance',
  },
  freight_forwarder: {
    url: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?auto=format&fit=crop&w=1800&q=85',
    alt: 'Container ship at sea',
    location: 'Freight forwarding operations',
    note: 'Ocean · air · customs · documents',
  },
};

const SERVICE_COLOR_CLASS: Record<string, string> = {
  brand: 'bg-brand-100 text-brand-700',
  ocean: 'bg-ocean-100 text-ocean-700',
  amber: 'bg-amber-100 text-amber-700',
  slate: 'bg-slate-100 text-slate-700',
};

function GeneratedHeroIllustration({ label, className = '' }: { label: string; className?: string }) {
  return (
    <div className={`bg-gradient-to-br from-brand-900 via-brand-700 to-ocean-500 ${className}`}>
      <svg viewBox="0 0 900 560" className="h-full w-full" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <defs>
          <pattern id="storefront-grid" width="42" height="42" patternUnits="userSpaceOnUse">
            <path d="M42 0H0v42" stroke="white" strokeOpacity="0.09" />
          </pattern>
        </defs>
        <rect width="900" height="560" fill="url(#storefront-grid)" />
        <path d="M70 430 C210 250 340 480 520 250 C650 90 760 150 850 70" stroke="white" strokeOpacity="0.36" strokeWidth="8" strokeLinecap="round" strokeDasharray="24 22" />
        <rect x="130" y="330" width="350" height="78" rx="10" fill="white" fillOpacity="0.18" />
        <rect x="508" y="358" width="122" height="70" rx="18" fill="white" fillOpacity="0.24" />
        <circle cx="208" cy="448" r="24" fill="white" fillOpacity="0.24" />
        <circle cx="548" cy="448" r="24" fill="white" fillOpacity="0.24" />
        <path d="M150 198H320M150 238H260M590 188H790M590 228H720" stroke="white" strokeOpacity="0.24" strokeWidth="10" strokeLinecap="round" />
        <rect x="628" y="300" width="138" height="96" rx="18" fill="white" fillOpacity="0.13" />
        <path d="M650 332H744M650 362H714" stroke="white" strokeOpacity="0.34" strokeWidth="8" strokeLinecap="round" />
        <text x="70" y="88" fill="white" fillOpacity="0.46" fontSize="24" fontWeight="700">{label}</text>
      </svg>
    </div>
  );
}

function flagToEmoji(flag: string): string {
  if (flag.length !== 2) return flag;
  return [...flag.toUpperCase()].map(char => String.fromCodePoint(127397 + char.charCodeAt(0))).join('');
}

export function HomePage() {
  const { brand, visual, home, routes, featureFlags } = useStorefront();
  const portalTheme = resolvePortalTheme(brand.portalThemeTemplate, visual);
  const stats = Array.isArray(home.stats) ? home.stats as Array<{ value: string; label: string }> : [];
  const services = Array.isArray(home.services) ? home.services as Array<{ title: string; desc: string; icon?: keyof typeof ICON_MAP; color?: string }> : [];
  const networkCountries = Array.isArray(home.networkCountries) ? home.networkCountries as Array<{ flag: string; country: string; note: string }> : [];
  const bookingHref = featureFlags.bookings === false ? null : '/book';
  const scheduleHref = featureFlags.sailingSchedule === false ? (featureFlags.tracking === false ? null : '/track') : '/schedule';
  const quickLinks = [
    featureFlags.tracking === false ? null : { to: '/track', label: 'Track Booking', icon: Search },
    featureFlags.bookings === false ? null : { to: '/book', label: 'Book Shipment', icon: PackagePlus },
    featureFlags.rates === false ? null : { to: '/rfq', label: 'Sales Inquiry / RFQ', icon: SearchCheck },
    featureFlags.otherInquiry === false ? null : { to: '/inquiry', label: 'Other Inquiry', icon: MessageSquare },
  ].filter(Boolean) as Array<{ to: string; label: string; icon: typeof Search }>;
  const fallbackMedia = INDUSTRY_HERO_MEDIA[visual.industryTemplate] ?? INDUSTRY_HERO_MEDIA.freight_forwarder;
  const generatedMedia = visual.mediaStrategy === 'generated_illustration';
  const heroMedia = visual.mediaStrategy === 'tenant_upload' && visual.heroMediaUrl
    ? {
        url: visual.heroMediaUrl,
        alt: visual.heroMediaAlt || `${brand.brandShortName} operations`,
        location: brand.brandShortName,
        note: String(home.heroBadge ?? brand.tagline),
      }
    : fallbackMedia;

  return (
    <div className="animate-fade-in">
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative text-white overflow-hidden min-h-[580px] flex items-center">
        {/* Real container ship background */}
        {generatedMedia ? (
          <GeneratedHeroIllustration label={heroMedia.location} className="absolute inset-0 h-full w-full" />
        ) : (
          <img
            src={heroMedia.url}
            alt={heroMedia.alt}
            className="absolute inset-0 w-full h-full object-cover object-center"
            loading="eager"
          />
        )}
        {/* Dark gradient overlay */}
        <div className="absolute inset-0" style={portalTheme.heroOverlay} />
        {/* Subtle dot overlay */}
        <div className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur text-white/90 text-xs font-medium px-3 py-1.5 rounded-full mb-5 border border-white/20">
                <Ship className="w-3.5 h-3.5" />
                {String(home.heroBadge ?? '')}
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold leading-tight mb-5">
                {String(home.heroTitle ?? brand.brandName)}
                <span className="block text-ocean-300">{String(home.heroAccent ?? brand.tagline)}</span>
              </h1>
              <p className="text-lg text-slate-300 mb-8 max-w-lg">
                {String(home.heroDescription ?? brand.tagline)}
              </p>

              {/* Stats row */}
              <div className="flex gap-6 mb-8">
                {stats.map(s => (
                  <div key={s.label} className="text-center">
                    <p className="text-2xl font-bold text-ocean-300">{s.value}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Inline track box */}
              <div className="bg-white/10 backdrop-blur rounded-xl p-5 max-w-md border border-white/20">
                <p className="text-sm text-slate-300 mb-3 font-medium">Track your booking</p>
                <form
                  onSubmit={e => {
                    e.preventDefault();
                    const fd = new FormData(e.currentTarget);
                    const ref = fd.get('ref')?.toString().trim();
                    if (ref) window.location.href = `/track/${encodeURIComponent(ref)}`;
                  }}
                  className="flex gap-2"
                >
                  <input
                    name="ref"
                    type="text"
                    placeholder="B/L No, container, or booking ref"
                    className="flex-1 px-4 py-3 rounded-lg bg-white text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-ocean-400"
                  />
                  <button
                    type="submit"
                    className="px-4 py-3 bg-ocean-500 hover:bg-ocean-400 rounded-lg transition-colors"
                    aria-label="Track"
                  >
                    <Search className="w-5 h-5" />
                  </button>
                </form>
              </div>
            </div>

            {/* Right — port photo card */}
            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-2 bg-ocean-500/30 rounded-3xl blur-2xl" />
                <div className="relative rounded-2xl overflow-hidden border border-white/20 shadow-2xl">
                  {generatedMedia ? (
                    <GeneratedHeroIllustration label={heroMedia.location} className="h-72 w-full" />
                  ) : (
                    <img
                      src={heroMedia.url}
                      alt={heroMedia.alt}
                      className="w-full h-72 object-cover"
                    />
                  )}
                  {/* Info overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-950/80 to-transparent p-5">
                    <div className="flex items-center gap-2 text-white">
                      <MapPin className="w-4 h-4 text-ocean-300" />
                      <span className="text-sm font-medium">{heroMedia.location}</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">{heroMedia.note}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Services ─────────────────────────────────────────────────────── */}
      <section className="py-12 bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {quickLinks.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-slate-900 shadow-sm transition hover:border-brand-300 hover:shadow-md"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold">{item.label}</p>
                    <p className="text-xs text-slate-500">Verified company access</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Services ─────────────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-ocean-600 text-sm font-semibold tracking-wide uppercase mb-2">What We Do</p>
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Complete Logistics Solutions</h2>
            <p className="text-slate-500 max-w-xl mx-auto">
              {String(home.heroDescription ?? brand.tagline)}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map(svc => {
              const Icon = ICON_MAP[svc.icon ?? 'ship'] ?? Ship;
              const colorClass = SERVICE_COLOR_CLASS[svc.color ?? 'brand'] ?? SERVICE_COLOR_CLASS.brand;
              return (
                <div
                  key={svc.title}
                  className="group p-6 bg-slate-50 rounded-xl hover:bg-white hover:shadow-md transition-all border border-transparent hover:border-slate-200"
                >
                  <div className={`w-11 h-11 rounded-lg ${colorClass} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{svc.title}</h3>
                  <p className="text-sm text-slate-500">{svc.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Network Banner ─────────────────────────────────────────────── */}
      <section className="relative py-16 overflow-hidden">
        {generatedMedia ? (
          <GeneratedHeroIllustration label={heroMedia.location} className="absolute inset-0 h-full w-full" />
        ) : (
          <img
            src={heroMedia.url}
            alt={heroMedia.alt}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0" style={portalTheme.heroOverlay} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-ocean-400 text-sm font-semibold tracking-wide uppercase mb-2">Our Network</p>
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-2">{String(home.networkTitle ?? 'Our Network')}</h2>
            <p className="text-slate-300 max-w-md mx-auto text-sm">{String(home.networkSubtitle ?? brand.tagline)}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {networkCountries.map((c, i) => (
              <div key={i} className="bg-white/10 backdrop-blur rounded-xl p-4 text-center border border-white/10 hover:bg-white/20 transition-colors">
                <p className="text-3xl mb-2">{flagToEmoji(c.flag)}</p>
                <p className="text-white font-medium text-sm">{c.country}</p>
                <p className="text-slate-400 text-xs mt-0.5">{c.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      <section className="py-16 bg-gradient-to-r from-brand-800 to-ocean-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold mb-3">{String(home.ctaTitle ?? `Ready to ship with ${brand.brandShortName}?`)}</h2>
          <p className="text-white/70 mb-8 max-w-lg mx-auto">
            {String(home.ctaDescription ?? `Get a freight quote or log in to your ${brand.brandShortName} customer portal.`)}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {bookingHref && (
              <Link
                to={bookingHref}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-brand-700 font-semibold rounded-lg hover:bg-brand-50 transition-colors"
              >
                Request Booking <ArrowRight className="w-4 h-4" />
              </Link>
            )}
            {scheduleHref && (
              <Link
                to={scheduleHref}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/20 transition-colors"
              >
                {featureFlags.sailingSchedule === false ? 'Track Booking' : 'Sailing Schedule'}
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
